import { IClientResponse } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/client';
import { subYears } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { flatMap, isArray, sortBy } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { CorporateActionsType, Document, DocumentType, OrderFilterType } from '../constants/lookups';
import { ApiInjectToken, MARKET_TIMEZONE, MiddlewareInjectToken } from '../constants/tokens';
import { HoldingInfo, MiddlewareConfig, Order, OrderWithExtraInfo, Sdks, TiprankOverviewWithExtraInfo } from '../types';
import {
    Account,
    AccountHistory,
    AccountHistoryWithExtraInfo,
    AccountWithExtraInfo,
    AccountWithPositionInfo,
    BalanceHistory,
    BankLink,
    CancelTransaction,
    CashTransfer,
    CryptoAccountInfo,
    CryptoStablecoinTransferStatusWithExtraInfo,
    DepositWithdraw,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import { AccountDriftReportItem } from '../types/DriftReport.type';
import { Group } from '../types/Group.type';
import { Model, ModelWithExtraInfo } from '../types/Model.type';
import { Portfolio, PortfolioWithExtraInfo, Position } from '../types/Portfolio.type';
import { formatDate } from '../utils';
import {
    formatAccountHistory,
    formatCorporateActions,
    formatDriftReport,
    formatHoldingInfo,
    formatOrders,
    formatPortfolio,
    formatPortfolioPositions,
} from '../utils/dataUtils';
import { sumBy } from '../utils/numberUtils';
import { CryptoOrderHistoryWithExtraInfo, CryptoTradingHistoryWithExtraInfo } from './../types/Account.type';
import { IBranchApi, IGroupApi, IModelApi, ITipranksApi, IUserApi } from './interfaces';
import { IAccountApi } from './interfaces/IAccountApi';
import { IQuoteApi } from './interfaces/IQuoteApi';

@injectable()
export class AccountApi extends CachedResponseApi implements IAccountApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) protected branchApi: IBranchApi,
        @inject(ApiInjectToken.ModelApi) protected modelApi: IModelApi,
        @inject(ApiInjectToken.GroupApi) protected groupApi: IGroupApi,
        @inject(ApiInjectToken.QuoteApi) protected quoteApi: IQuoteApi,
        @inject(ApiInjectToken.UserApi) protected userApi: IUserApi,
        @inject(ApiInjectToken.TipranksApi) protected tipranksApi: ITipranksApi,
    ) {
        super(sdks, config, cache);
    }

    async getAccounts(cryptoOnly?: boolean): Promise<Account[]> {
        const response = await this.userApi.getUserInfo();
        if (cryptoOnly) {
            return response.accountList.filter((account) => account.cryptoEnabled);
        } else {
            return response.accountList;
        }
    }

    async getAccount(accountId: number): Promise<AccountWithExtraInfo | null> {
        const responses = await Promise.all([
            this.getAccounts(),
            this.sdks.orbis.get('user/rtb', undefined, { params: { accountId } }),
        ]);
        const accounts = responses[0];
        const account = accounts.find((account) => account.accountId === accountId);

        if (!account) {
            return null;
        }

        const {
            balance = 0,
            totalDividendAmount = 0,
            totalDividendYield = 0,
            sodCashBalance = 0,
            sodMarginBalance = 0,
            sodShortBalance = 0,
            cashOnAccount = 0,
            markToMarket = 0,
        } = responses[1].data || {};

        return {
            ...account,
            balance,
            totalDividendAmount,
            totalDividendYield,
            sodCashBalance,
            sodMarginBalance,
            sodShortBalance,
            cashOnAccount,
            markToMarket,
        };
    }

    async getPortfolio(accountId: number, symbol?: string, tipranks?: boolean): Promise<PortfolioWithExtraInfo> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor ? this.branchApi.getAccount(accountId) : this.getAccount(accountId),
            this.sdks.orbis.get('user/portfolio/full', undefined, {
                params: {
                    loadQuotes: true,
                    loadCost: true,
                    loadIndustries: true,
                    accountId,
                },
            }),
        ]);
        let tipranksData: TiprankOverviewWithExtraInfo[] = [];

        const account = data[0];
        const portfolio = data[1].data as Portfolio;

        if (tipranks && portfolio.entries.length > 0) {
            if (symbol) {
                tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                    portfolio.entries.map((entry: Position) => entry.symbol).filter((entry) => entry === symbol),
                );
            }
            tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                portfolio.entries.map((entry: Position) => entry.symbol),
            );
        }

        return formatPortfolio(portfolio, account ? [account] : [], [], account?.balance || 0, {
            symbol,
            tipranks: tipranksData,
        });
    }

    async getOrders(
        accountId: number,
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
        symbol?: string,
        option?: boolean,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor
                ? this.sdks.orbis.get('v2/advisory/models')
                : Promise.resolve({ data: [] }),
            this.config.apiPermissions.advisor ? this.branchApi.getInventoryAccounts() : Promise.resolve([]),
            this.getAccounts(),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.sdks.orbis.get('orders', undefined, {
                    urlQuery: filter,
                    params: { accountId, loadQuotes: true, symbol, option: option },
                });
            }),
        ]);

        const models = data[0].data as Model[];
        const inventoryAccounts = data[1] as Account[];
        const accounts = data[2];
        const orders = flatMap(data.slice(3) as IClientResponse[], (item) => item.data) as Order[];

        return formatOrders(orders, models, accounts, inventoryAccounts[0]);
    }

    async getModelAndGroups(accountId: number): Promise<{
        model: ModelWithExtraInfo | null;
        groups: Group[];
    }> {
        const data = await Promise.all([this.modelApi.getModels(), this.groupApi.getGroups()]);

        const model =
            data[0].find((model) => model.accounts.map((account) => account.accountId).includes(accountId)) || null;
        const groups = data[1].filter((group) => group.accountIds.includes(accountId));

        return {
            model,
            groups,
        };
    }

    async getCorporateActions(
        accountId: number,
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateActionWithExtraInfo[]> {
        const positions = (await this.getPortfolio(accountId)).entries;
        const actions = (
            await this.sdks.orbis.get('research/actions/search', undefined, {
                params: {
                    symbol: positions.map((position) => position.symbol)?.join(','),
                    dateFrom,
                    dateTo,
                    types,
                },
            })
        ).data as CorporateAction[];

        return formatCorporateActions(actions, positions);
    }

    async getDriftReport(accountId: number): Promise<AccountDriftReportItem[]> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor ? this.branchApi.getAccount(accountId) : this.getAccount(accountId),
            this.getPortfolio(accountId),
            this.modelApi.getModels(),
        ]);

        const account = data[0];
        const positions = data[1].entries;
        const model = data[2].find((model) =>
            model.accounts.map((account) => account.accountId).includes(account?.accountId || 0),
        );

        if (!account) {
            return [];
        }

        return formatDriftReport(account, positions, model);
    }

    async getHoldingInfo(accountId: number, symbol: string): Promise<HoldingInfo> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor ? this.branchApi.getAccount(accountId) : this.getAccount(accountId),
            this.getPortfolio(accountId),
            this.modelApi.getModels(),
        ]);

        const account = data[0];
        const positions = data[1].entries;
        const model = data[2].find((model) =>
            model.accounts.map((account) => account.accountId).includes(account?.accountId || 0),
        );
        const modelComponent = model?.components?.filter((component) => component.symbol === symbol)[0];

        return formatHoldingInfo(symbol, positions, account?.balance || 0, modelComponent?.percentage || 0);
    }

    async getAccountWithPositionInfo(symbol: string, ...accountIds: number[]): Promise<AccountWithPositionInfo[]> {
        const accounts = (await this.branchApi.getAccounts()).filter((account) =>
            accountIds.includes(account.accountId),
        );
        const availableCashes = (
            await Promise.all(
                accountIds.map((accountId) =>
                    this.sdks.orbis.get('user/balance', undefined, { params: { accountId } }),
                ),
            )
        ).map((item) => item.data.availableCash) as number[];
        const portfolios = (
            await Promise.all(
                accountIds.map((accountId) =>
                    this.sdks.orbis.get('user/portfolio/full', undefined, {
                        params: {
                            loadQuotes: true,
                            loadCost: true,
                            loadIndustries: true,
                            accountId,
                        },
                    }),
                ),
            )
        ).map((item) => item.data) as Portfolio[];

        return accountIds.map((accountId, index) => {
            // we are sure that account exists
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const account = accounts.find((account) => account.accountId === accountId)!;
            const portfolio = portfolios[index];
            const positions = formatPortfolioPositions(
                portfolio.entries,
                account ? [account] : [],
                [],
                account?.balance || 0,
                symbol,
            );

            return {
                ...account,
                availableCash: availableCashes[index],
                positionValue: sumBy(positions, (position) => position.value),
                positionWeight: sumBy(positions, (position) => position.weight),
                positionShares: sumBy(positions, (position) => position.quantity),
                portfolio: {
                    ...portfolio,
                    entries: positions,
                },
            };
        });
    }

    async getAccountHistory(
        accountId: number,
        dateFrom: string,
        dateTo: string,
    ): Promise<AccountHistoryWithExtraInfo[]> {
        const history = (
            await this.sdks.orbis.get('user/account/history', undefined, {
                params: {
                    accountId,
                    dateFrom,
                    dateTo,
                },
            })
        ).data as AccountHistory[];

        return formatAccountHistory(history);
    }

    async getAccountPerformance(
        accountId: number,
        dateFrom: string,
        withTodaysBalance?: boolean,
    ): Promise<BalanceHistory[]> {
        const [balanceHistory, account] = await Promise.all([
            this.sdks.orbis.get('user/rtb/history', undefined, {
                params: {
                    accountId,
                    dateFrom,
                    dateTo: format(new Date(), 'MM/dd/yyyy'),
                },
            }),
            withTodaysBalance ? this.getAccount(accountId) : Promise.resolve(null),
        ]);

        return (balanceHistory.data as BalanceHistory[]).concat(
            account
                ? [
                      {
                          balance: account.balance,
                          date: format(utcToZonedTime(new Date(), MARKET_TIMEZONE), 'MM/dd/yyyy HH:mm:ss z', {
                              timeZone: MARKET_TIMEZONE,
                          }),
                      },
                  ]
                : [],
        );
    }

    async getAccountBuyingPower(accountId: string | number): Promise<number> {
        const response = await this.sdks.orbis.get('user/balance', undefined, { params: { accountId } });
        return response.data.availableCash;
    }

    async getAccountDocuments(accountNumber: string, docType: DocumentType): Promise<Document[]> {
        const beginDate = subYears(new Date(), 10);
        const documents = (
            await this.sdks.orbis.get('documents/service', undefined, {
                params: {
                    accountNumber,
                    docType,
                    beginMonth: formatDate(beginDate, { pattern: 'MM' }),
                    beginYear: formatDate(beginDate, { pattern: 'yyyy' }),
                    endMonth: formatDate(new Date(), { pattern: 'MM' }),
                    endYear: formatDate(new Date(), { pattern: 'yyyy' }),
                },
            })
        ).data?.documentList as Document[];
        return sortBy(documents, (document) => new Date(document.documentDate).getTime());
    }

    // Plaid related
    async getTempIavKey(): Promise<string> {
        const data = this.config.apiPermissions.iavProcessorToken ? { processorToken: true } : undefined;
        const response = await this.sdks.orbis.post('iav/init', data );
        return response.data.publicKey;
    }

    // Plaid related
    async iavComplete(accountNumber: string, publicToken: string, bankAccountId: string): Promise<BankLink> {
        const data =  { account: accountNumber, publicToken, bankAccountId};
        const response = await this.sdks.orbis.post('iav/complete',  this.config.apiPermissions.iavProcessorToken ? {...data, processorToken: true } : data);
        return response.data;
    }

    // Plaid related
    async getAccountCashTransfers(accountNumber: string): Promise<CashTransfer[]> {
        const response = await this.sdks.orbis.get('transfer/status', undefined, { urlQuery: accountNumber });
        return response.data;
    }

    // Plaid related
    async getAccountBankLinks(accountNumber: string): Promise<BankLink[]> {
        const response = await this.sdks.orbis.get('transfer/relationship/list', undefined, {
            urlQuery: accountNumber,
        });
        return response.data;
    }

    // Plaid related
    async getAccountWithdrawableCash(accountNumber: string): Promise<number> {
        const response = await this.sdks.orbis.get('transfer/available', undefined, { urlQuery: accountNumber });
        return response.data;
    }

    // Plaid related
    async cancelBankLink(accountNumber: string, entryId: number): Promise<BankLink> {
        const response = await this.sdks.orbis.post(
            'transfer/relationship/cancel',
            { account: accountNumber, reason: 'delete' },
            { urlQuery: entryId },
        );
        return response.data;
    }

    async depositCash(accountNumber: string, entryId: number, amount: number): Promise<DepositWithdraw> {
        const response = await this.sdks.orbis.post('transfer/ach/deposit', {
            account: accountNumber,
            entryId,
            amount,
        });
        return response.data;
    }

    async withdrawCash(accountNumber: string, entryId: number, amount: number): Promise<DepositWithdraw> {
        const response = await this.sdks.orbis.post('transfer/ach/withdraw', {
            account: accountNumber,
            entryId,
            amount,
        });
        return response.data;
    }

    async cancelTransaction(accountNumber: string, transferId: string): Promise<CancelTransaction> {
        const response = await this.sdks.orbis.post('transfer/ach/cancel', { account: accountNumber, transferId });
        return response.data;
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    async getAccountCryptoBuyingPower(accountId: number, accountNumber: string): Promise<number> {
        return this.getAccountBuyingPower(accountId);
    }

    async getAccountCryptoAssets(accountId: number, accountNumber: string): Promise<PortfolioWithExtraInfo> {
        return this.getPortfolio(accountId, accountNumber);
    }

    getAccountCryptoAccountOrderHistory(accountNumber: string): Promise<CryptoOrderHistoryWithExtraInfo[]> {
        throw new Error('Not supported');
    }

    getAccountCryptoAccountTradingHistory(accountNumber: string): Promise<CryptoTradingHistoryWithExtraInfo[]> {
        throw new Error('Not supported');
    }

    purchaseCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        throw new Error('Not supported');
    }

    redeemCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        throw new Error('Not supported');
    }

    getCryptoAccountStablecoinTransferStatuses(
        accountNumber: string,
    ): Promise<CryptoStablecoinTransferStatusWithExtraInfo[]> {
        throw new Error('Not supported');
    }

    getAccountCryptoOpenOrders(accountNumber: string): Promise<CryptoOrderHistoryWithExtraInfo[]> {
        throw new Error('Not supported');
    }

    getCryptoAccountAvailablePdfStatementDates(accountNumber: string): Promise<{ from: Date; to: Date }> {
        throw new Error('Not supported');
    }

    generateCryptoAccountPdfStatement(accountNumber: string, date: string): Promise<Blob> {
        throw new Error('Not supported');
    }

    getCryptoAccountOrdersCsv(accountNumber: string): Promise<Blob> {
        throw new Error('Not supported');
    }

    getCryptoAccountTransfersCsv(accountNumber: string): Promise<Blob> {
        throw new Error('Not supported');
    }

    getCryptoAccountInfo(accountNumber: string): Promise<CryptoAccountInfo> {
        throw new Error('Not supported');
    }

    /* eslint-enable @typescript-eslint/no-unused-vars */
}
